import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";
import type { Automation, AutomationCatalog, AutomationRun, AutomationScope, SchedulePreview, WatcherPreview } from "@ncp/assistant-sdk";
import type { DefinitionVersion } from "../forms/api";
import type { ChangeSet } from "../releases/api";

export type { Automation, AutomationCatalog, AutomationRun, AutomationScope, SchedulePreview, WatcherPreview };

export interface AutomationRef { key: string; name: string; nameAr?: string | null; group?: string | null; workspaces?: string[] | null }

/** The playbooks an automation of this scope can use: a workspace automation only sees its own workspace's playbooks; others see all. */
export function playbooksFor(options: AutomationEditorOptions | undefined, scope: AutomationScope, workspaceKey: string | null | undefined, dynamicOnly = false): AutomationRef[] {
  const all = (options?.playbooks ?? []).filter((p) => !dynamicOnly || p.group === "Dynamic");
  return scope === "Workspace" && workspaceKey ? all.filter((p) => p.workspaces?.includes(workspaceKey)) : all;
}

export interface AutomationConnectorParameter { name: string; label?: string | null; labelAr?: string | null; description?: string | null; required: boolean; default?: string | null; example?: string | null }
export interface AutomationConnectorOperation { key: string; name: string; nameAr?: string | null; description?: string | null; readOnly: boolean; parameters: AutomationConnectorParameter[]; returnsTable: boolean }
export interface AutomationConnector { key: string; name: string; nameAr?: string | null; operations: AutomationConnectorOperation[] }

/** Everything the editor's pickers need (`GET /api/platform/automations/options`). */
export interface AutomationEditorOptions {
  catalog: AutomationCatalog;
  playbooks: AutomationRef[];
  agents: AutomationRef[];
  roles: AutomationRef[];
  workspaces: AutomationRef[];
  /** Published connectors with their operations (Connector Builder). */
  connectors: AutomationConnector[];
  /** Enabled webhook subscriptions (`group` carries the URL). */
  webhooks: AutomationRef[];
  recordTypes?: AutomationRef[];
  letterTemplates?: AutomationRef[];
}

export interface AutomationDetail { automation: Automation; versions: DefinitionVersion[]; runs: AutomationRun[] }

export type AutomationAction = { type: string } & Record<string, unknown>;

/** The editable part of an automation, as sent to `PUT /api/platform/automations/{id}` and `POST /check`. */
export interface AutomationBody {
  name: string;
  nameAr: string | null;
  description: string | null;
  trigger: string;
  triggerConfig: Record<string, unknown>;
  condition: unknown;
  actions: AutomationAction[];
  serviceKey: string | null;
  enabled: boolean;
  throttleMinutes: number;
  scope: AutomationScope;
  workspaceKey: string | null;
}

export const SCHEDULE_TRIGGER = "schedule.cron";
/** Triggers without an event from the catalogue; only platform automations use them. */
export const PLATFORM_TRIGGERS = ["manual", "webhook.inbound"] as const;

export function automationBodyOf(a: Automation): AutomationBody {
  return {
    name: a.name, nameAr: a.nameAr ?? null, description: a.description ?? null, trigger: a.trigger,
    triggerConfig: (a.triggerConfig as Record<string, unknown> | null) ?? {}, condition: a.condition ?? null,
    actions: ((a.actions ?? []) as AutomationAction[]), serviceKey: a.serviceKey ?? null, enabled: a.enabled, throttleMinutes: a.throttleMinutes,
    scope: a.scope, workspaceKey: a.workspaceKey ?? null,
  };
}

export function emptyAutomation(name: string, nameAr: string | null, scope: AutomationScope, workspaceKey: string | null, schedule: boolean): AutomationBody {
  return {
    name, nameAr, description: null,
    trigger: schedule ? SCHEDULE_TRIGGER : "case.created",
    triggerConfig: schedule ? { schedule: "0 7 * * 1-5" } : { event: "matter.opened" },
    condition: null,
    actions: [{ type: "notification.send", to: scope === "Workspace" ? "role:Director" : "assignee", text: null }],
    serviceKey: null, enabled: true, throttleMinutes: 0, scope, workspaceKey: scope === "Workspace" ? workspaceKey : null,
  };
}

const AU = ["platform", "automations"] as const;

export function useAutomations(scope?: string, q?: string) {
  const api = useApi();
  const params = new URLSearchParams();
  if (scope) params.set("scope", scope);
  if (q) params.set("q", q);
  const qs = params.toString();
  return useQuery({ queryKey: [...AU, "list", scope ?? "", q ?? ""], queryFn: () => api.get<Automation[]>(`/api/platform/automations${qs ? `?${qs}` : ""}`), staleTime: 10_000 });
}

export function useAutomation(key: string | undefined, version?: number) {
  const api = useApi();
  return useQuery({
    queryKey: [...AU, "one", key, version ?? "latest"],
    queryFn: () => api.get<AutomationDetail>(`/api/platform/automations/${encodeURIComponent(key!)}${version ? `?version=${version}` : ""}`),
    enabled: !!key,
    staleTime: 5_000,
    retry: false,
  });
}

export function useAutomationOptions() {
  const api = useApi();
  return useQuery({ queryKey: [...AU, "options"], queryFn: () => api.get<AutomationEditorOptions>("/api/platform/automations/options"), staleTime: 60_000 });
}

export function useAutomationRuns(key?: string, status?: string, take = 100) {
  const api = useApi();
  const params = new URLSearchParams({ take: String(take) });
  if (key) params.set("key", key);
  if (status) params.set("status", status);
  return useQuery({ queryKey: [...AU, "runs", key ?? "", status ?? "", take], queryFn: () => api.get<AutomationRun[]>(`/api/platform/automations/runs?${params}`), refetchInterval: 30_000 });
}

export function useConsoleSchedulePreview(expression: string, timeZone: string | null) {
  const api = useApi();
  const params = new URLSearchParams({ expression });
  if (timeZone) params.set("timeZone", timeZone);
  return useQuery({
    queryKey: [...AU, "schedule", expression, timeZone ?? ""],
    queryFn: () => api.get<SchedulePreview>(`/api/platform/automations/schedule-preview?${params}`),
    enabled: expression.trim().length > 0,
    staleTime: 60_000,
  });
}

/** The server's reading of an unsaved body: plain-language when/then, next runs and the problems a publish would reject. */
export function useAutomationCheck(body: AutomationBody | null) {
  const api = useApi();
  const json = body ? JSON.stringify(body) : "";
  return useQuery({
    queryKey: [...AU, "check", json],
    queryFn: () => api.post<WatcherPreview>("/api/platform/automations/check", body),
    enabled: !!body,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAutomationActions() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [...AU] });
    void qc.invalidateQueries({ queryKey: ["platform", "change-sets"] });
    void qc.invalidateQueries({ queryKey: ["platform", "definitions", "automations"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string; body: AutomationBody; changeNote?: string }) => api.post<Automation>("/api/platform/automations", input), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string; body: AutomationBody; changeNote?: string }) => api.put<Automation>(`/api/platform/automations/${input.id}`, { body: input.body, changeNote: input.changeNote }), onSuccess: invalidate }),
    newVersion: useSave({ mutationFn: (input: { key: string; changeNote?: string }) => api.post<Automation>(`/api/platform/automations/${encodeURIComponent(input.key)}/versions`, { changeNote: input.changeNote }), onSuccess: invalidate }),
    discard: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/automations/${id}`), onSuccess: invalidate }),
    stage: useSave({ mutationFn: (input: { id: string; titleHint?: string }) => api.post<ChangeSet>(`/api/platform/automations/${input.id}/stage`, { titleHint: input.titleHint }), onSuccess: invalidate }),
    withdraw: useSave({ mutationFn: (id: string) => api.post<Automation>(`/api/platform/automations/${id}/withdraw`), onSuccess: invalidate }),
    setEnabled: useSave({ mutationFn: (input: { id: string; enabled: boolean }) => api.post<Automation>(`/api/platform/automations/${input.id}/enabled`, { enabled: input.enabled }), onSuccess: invalidate }),
    run: useSave({
      mutationFn: (input: { id: string; caseId?: string | null; caseNumber?: string | null; dryRun: boolean }) =>
        api.post<AutomationRun>(`/api/platform/automations/${input.id}/run`, { caseId: input.caseId ?? null, caseNumber: input.caseNumber ?? null, dryRun: input.dryRun }),
      // A run (or a dry run, which changes nothing) is not a save of the automation.
      failed: t("admin:common.error"),
      onSuccess: invalidate,
    }),
  };
}
